import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  Drawer,
  FormControl,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import { font } from "../../styles/font";
import { AddOutlined, CloseOutlined } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import CustomEditor from "../common/CustomEditor";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useApplicationContext } from "../../context/ApplicationContext";
import { createTask_API } from "../../api/controller/TaskController";
import { HttpStatusCode } from "axios";
import { Severity } from "../../constant/ApplicationVariables";
import useErrorHandler from "../../api/errorHandler/useErrorHandler";

const TaskStatus = {
  todo: "To do",
  inProgress: "In Progress",
  needReview: "Need Review",
  done: "Done",
};

export default function CreateTaskComponent({ open, onClose, handleConfirm }) {
  const { activeProject, handleSnackbar } = useApplicationContext();
  const theme = useTheme();
  const errorHandler = useErrorHandler();
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [taskData, setTaskData] = useState({
    title: "",
    description: "",
  });
  const [error, setError] = useState({});

  const handleClose = () => {
    setTaskData({ title: "", description: "" });
    setError({});
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleValidateInputs();
  };

  const handleValidateInputs = () => {
    let newError = {};

    if (!taskData?.title?.trim()) {
      newError.title = "Title is required";
    }

    if (!taskData?.description?.trim()) {
      newError.description = "Description is required";
    }

    setError(newError);
    if (Object.keys(newError).length > 0) return;

    return handleContinue(taskData);
  };

  const handleChange = (e) => {
    setTaskData({ ...taskData, [e.target.name]: e.target.value });
    // Clear error for this field when user starts typing
    if (error[e.target.name]) {
      setError({ ...error, [e.target.name]: "" });
    }
  };

  const handleContinue = async () => {
    try {
      setIsLoading(true);

      let body = {
        title: taskData?.title?.trim(),
        description: taskData?.description?.trim(),
        projectName: activeProject?.name,
      };

      const response = await createTask_API(body);

      if (response.status === HttpStatusCode.Ok) {
        handleSnackbar(
          response?.message || "Task created successfully",
          Severity.success,
        );
        handleConfirm();
        handleClose();
      }

      if (response) {
        errorHandler(response, setIsLoading);
      }
    } catch (error) {
      console.log(error, "projectreponse errorr");
      handleSnackbar("Failed to create task", Severity.error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      anchor="right"
      PaperProps={{
        sx: {
          width: 420,
          backgroundColor: theme.palette.background.paper,
        },
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        className="flex flex-col h-full"
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            Create New Task
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: alpha(theme.palette.error.main, 0.1),
                color: theme.palette.error.main,
              },
            }}
          >
            <CloseOutlined />
          </IconButton>
        </Box>

        {/* Body */}
        <Box
          sx={{
            flex: 1,
            p: 3,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {/* Title Field */}
          <FormControl fullWidth>
            <FormLabel
              sx={{
                mb: 1,
                color: theme.palette.text.primary,
                fontWeight: 500,
                fontSize: "0.875rem",
              }}
            >
              Title *
            </FormLabel>
            <TextField
              name="title"
              placeholder="Enter task title"
              value={taskData?.title}
              onChange={handleChange}
              error={!!error?.title}
              helperText={error?.title}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.palette.background.default,
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.primary.main,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2,
                  },
                },
              }}
            />
          </FormControl>

          {/* Description Field */}
          <FormControl fullWidth>
            <FormLabel
              sx={{
                mb: 1,
                color: theme.palette.text.primary,
                fontWeight: 500,
                fontSize: "0.875rem",
              }}
            >
              Description *
            </FormLabel>
            <TextField
              name="description"
              placeholder="Enter task description"
              value={taskData?.description}
              onChange={handleChange}
              error={!!error?.description}
              helperText={error?.description}
              multiline
              minRows={4}
              maxRows={8}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.palette.background.default,
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.primary.main,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2,
                  },
                },
              }}
            />
          </FormControl>

          {/* Project Info (Read-only) */}
          {activeProject && (
            <Box
              sx={{
                p: 2,
                borderRadius: theme.shape.borderRadius,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  display: "block",
                  mb: 0.5,
                }}
              >
                Project
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 500,
                }}
              >
                {activeProject?.name}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 3,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={handleClose}
            disabled={isLoading}
            sx={{
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,

              "&:hover": {
                borderColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={
              isLoading ||
              !taskData.title.trim() ||
              !taskData.description.trim()
            }
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.mode === "dark" ? "#000" : "#fff",
              fontWeight: 600,

              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },

              "&.Mui-disabled": {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            }}
          >
            {isLoading ? "Creating..." : "Create Task"}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

function MemberCard({ user }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
        borderRadius: theme.shape.borderRadius,
        backgroundColor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.background.default, 0.5)
            : theme.palette.grey[50],
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <Checkbox />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          width: "100%",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 500,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {user?.name}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {user?.email}
        </Typography>
      </Box>
    </Box>
  );
}
